/**
 * AI Readiness Lead Magnet Form
 *
 * Captures a work email, basic identity context, and required consent
 * before requesting an AI Readiness evaluation.
 *
 * Primary responsibilities:
 * - Collect email-first marketing lead data
 * - Enforce required verification and consent checkboxes
 * - Submit the public AI readiness request and show delivery status
 *
 * Dependencies:
 * - Shared lead magnet identity helpers
 * - Shadcn form controls
 */

'use client'

import { useId, useState } from 'react'
import {
  ArrowRightIcon,
  CheckCircledIcon,
  EnvelopeClosedIcon,
  ExclamationTriangleIcon,
  ReloadIcon
} from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'

import {
  AiReadinessLeadMagnetSource,
  extractLeadMagnetEmailDomain,
  isRestrictedLeadMagnetEmailDomain
} from '@/lib/quick-ai-readiness/lead-magnet'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { getMarketingCopy } from '@/lib/marketing-copy'

interface AiReadinessLeadMagnetFormProps {
  source: AiReadinessLeadMagnetSource
  className?: string
  variant?: 'compact' | 'modal' | 'page'
}

interface SubmissionSuccessState {
  businessName: string
  domain: string
  email: string
}

const accentIconClassName =
  'bg-[rgb(236,240,247)] text-[rgb(60,82,110)] dark:bg-sky-500/15 dark:text-sky-200'

// --------------

function readCampaignParams() {
  if (typeof window === 'undefined') {
    return {}
  }

  const searchParams = new URLSearchParams(window.location.search)

  return {
    utmSource: searchParams.get('utm_source') || undefined,
    utmMedium: searchParams.get('utm_medium') || undefined,
    utmCampaign: searchParams.get('utm_campaign') || undefined,
    utmTerm: searchParams.get('utm_term') || undefined,
    utmContent: searchParams.get('utm_content') || undefined
  }
}

// --------------

function formatMarketingTemplate(
  template: string,
  values: Record<string, string>
) {
  return Object.entries(values).reduce((result, [key, value]) => {
    return result.replaceAll(`{{${key}}}`, value)
  }, template)
}

// --------------

export function AiReadinessLeadMagnetForm({
  source,
  className,
  variant = 'compact'
}: AiReadinessLeadMagnetFormProps) {
  const formId = useId()
  const { i18n } = useTranslation()
  const copy = getMarketingCopy(i18n.resolvedLanguage || i18n.language)
  const formCopy = copy.leadMagnet.form
  const isModalVariant = variant === 'modal'
  const isPageVariant = variant === 'page'
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [websiteOverride, setWebsiteOverride] = useState('')
  const [deliveryConfirmed, setDeliveryConfirmed] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [modalConsentConfirmed, setModalConsentConfirmed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState<SubmissionSuccessState | null>(null)

  const derivedDomain = (() => {
    if (!email.includes('@')) {
      return null
    }

    try {
      return extractLeadMagnetEmailDomain(email)
    } catch {
      return null
    }
  })()

  const usesRestrictedDomain = derivedDomain
    ? isRestrictedLeadMagnetEmailDomain(derivedDomain)
    : false

  const resetForm = () => {
    setFullName('')
    setEmail('')
    setJobTitle('')
    setWebsiteOverride('')
    setDeliveryConfirmed(false)
    setMarketingConsent(false)
    setModalConsentConfirmed(false)
    setError('')
    setSuccess(null)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')

    if (!email.trim()) {
      setError(formCopy.enterEmail)
      return
    }

    if (!derivedDomain) {
      setError(formCopy.enterValidEmail)
      return
    }

    if (usesRestrictedDomain) {
      setError(formCopy.restrictedDomain)
      return
    }

    if (isModalVariant) {
      if (!modalConsentConfirmed) {
        setError(formCopy.confirmDelivery)
        return
      }
    } else {
      if (!fullName.trim()) {
        setError(formCopy.enterName)
        return
      }

      if (!jobTitle.trim()) {
        setError(formCopy.enterRole)
        return
      }

      if (!deliveryConfirmed) {
        setError(formCopy.confirmDelivery)
        return
      }

      if (!marketingConsent) {
        setError(formCopy.consentRequired)
        return
      }
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/quick-ai-readiness/lead-magnet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          fullName: isModalVariant ? undefined : fullName,
          jobTitle: isModalVariant ? undefined : jobTitle,
          websiteOverride: isModalVariant ? undefined : websiteOverride,
          deliveryConfirmed: isModalVariant ? undefined : deliveryConfirmed,
          marketingConsent: isModalVariant ? undefined : marketingConsent,
          consentConfirmed: isModalVariant ? modalConsentConfirmed : undefined,
          submissionVariant: isModalVariant ? 'modal' : isPageVariant ? 'page' : 'compact',
          source,
          sourcePath: window.location.pathname,
          referrerUrl: document.referrer || undefined,
          ...readCampaignParams()
        })
      })

      const payload = await response.json()

      if (!response.ok) {
        throw new Error(
          typeof payload?.error === 'string'
            ? payload.error
            : formCopy.submitError
        )
      }

      setSuccess({
        businessName: payload.businessName,
        domain: payload.domain,
        email: payload.email
      })
    } catch (submissionError) {
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : formCopy.submitError
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (success) {
    return (
      <div
        className={cn(
          isModalVariant
            ? 'h-fit rounded-[1.75rem] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(243,246,250,0.96))] p-5 sm:p-6 lg:p-7 dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(14,20,30,0.98),rgba(8,13,20,0.96))]'
            : 'rounded-[2rem] border border-slate-200/80 bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-[rgba(9,14,22,0.96)] dark:shadow-[0_24px_80px_rgba(0,0,0,0.32)]',
          className
        )}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
          <div className={`flex size-12 shrink-0 items-center justify-center rounded-2xl ${accentIconClassName}`}>
            <CheckCircledIcon className="size-6" />
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                {formCopy.successLabel}
              </p>
              <h3 className="mt-2 text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl dark:text-slate-50">
                {formCopy.successTitle}
              </h3>
            </div>
            <p className="max-w-[52ch] text-sm leading-7 text-slate-600 dark:text-slate-300">
              {formatMarketingTemplate(formCopy.successDescription, {
                businessName: success.businessName,
                domain: success.domain,
                email: success.email
              })}
            </p>
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-full border-slate-300 sm:w-auto dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
              onClick={resetForm}
            >
              {formCopy.checkAnother}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        isModalVariant
          ? 'h-fit rounded-[1.75rem] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(243,246,250,0.96))] p-5 sm:p-6 lg:p-7 dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(14,20,30,0.98),rgba(8,13,20,0.96))]'
          : 'rounded-[2rem] border border-slate-200/80 bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.08)] sm:p-7 dark:border-white/10 dark:bg-[rgba(9,14,22,0.96)] dark:shadow-[0_24px_80px_rgba(0,0,0,0.32)]',
        className
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
        <div className={`flex size-11 shrink-0 items-center justify-center rounded-2xl ${accentIconClassName}`}>
          <EnvelopeClosedIcon className="size-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            {formCopy.eyebrow}
          </p>
          <h3 className="mt-2 text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl dark:text-slate-50">
            {isModalVariant ? formCopy.modalTitle : formCopy.title}
          </h3>
          <p className="mt-2 max-w-[52ch] text-sm leading-7 text-slate-600 dark:text-slate-300">
            {isModalVariant ? formCopy.modalDescription : formCopy.description}
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-5">
        {!isModalVariant ? (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label
                htmlFor={`ai-readiness-full-name-${formId}`}
                className="text-sm font-medium text-slate-900 dark:text-slate-100"
              >
                {formCopy.fullName}
              </Label>
              <Input
                id={`ai-readiness-full-name-${formId}`}
                type="text"
                autoComplete="name"
                placeholder={formCopy.fullNamePlaceholder}
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                disabled={isSubmitting}
                className="h-12 rounded-2xl border-slate-200 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-400"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor={`ai-readiness-job-title-${formId}`}
                className="text-sm font-medium text-slate-900 dark:text-slate-100"
              >
                {formCopy.role}
              </Label>
              <Input
                id={`ai-readiness-job-title-${formId}`}
                type="text"
                autoComplete="organization-title"
                placeholder={formCopy.rolePlaceholder}
                value={jobTitle}
                onChange={(event) => setJobTitle(event.target.value)}
                disabled={isSubmitting}
                className="h-12 rounded-2xl border-slate-200 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-400"
              />
            </div>
          </div>
        ) : null}

        <div className="space-y-2">
          <Label
            htmlFor={`ai-readiness-email-${formId}`}
            className="text-sm font-medium text-slate-900 dark:text-slate-100"
          >
            {formCopy.companyEmail}
          </Label>
          <Input
            id={`ai-readiness-email-${formId}`}
            type="email"
            autoComplete="email"
            placeholder={formCopy.companyEmailPlaceholder}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={isSubmitting}
            className="h-12 rounded-2xl border-slate-200 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-400"
          />
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            {derivedDomain ? (
              <span
                className={cn(
                  'rounded-full px-3 py-1 font-medium',
                  usesRestrictedDomain
                    ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-200'
                    : 'bg-[rgb(236,240,247)] text-[rgb(60,82,110)] dark:bg-sky-500/15 dark:text-sky-200'
                )}
              >
                {usesRestrictedDomain
                  ? formCopy.useCompanyInbox
                  : formatMarketingTemplate(formCopy.companyDomainBadge, {
                      domain: derivedDomain
                    })}
              </span>
            ) : null}
            <span>{formCopy.freeMailHint}</span>
          </div>
        </div>

        {!isModalVariant ? (
          <div className="space-y-2">
            <Label
              htmlFor={`ai-readiness-website-${formId}`}
              className="text-sm font-medium text-slate-900 dark:text-slate-100"
            >
              {formCopy.websiteOverride}
              <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
                {formCopy.optional}
              </span>
            </Label>
            <Input
              id={`ai-readiness-website-${formId}`}
              type="text"
              placeholder={formCopy.websitePlaceholder}
              value={websiteOverride}
              onChange={(event) => setWebsiteOverride(event.target.value)}
              disabled={isSubmitting}
              className="h-12 rounded-2xl border-slate-200 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-400"
            />
            <p className="text-xs leading-6 text-slate-500 dark:text-slate-400">
              {formCopy.websiteHint}
            </p>
          </div>
        ) : null}

        <div className="space-y-4 rounded-3xl border border-slate-200 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-white/5">
          {isModalVariant ? (
            <div className="flex items-start gap-3">
              <Checkbox
                id={`modal-consent-confirmed-${formId}`}
                checked={modalConsentConfirmed}
                onCheckedChange={(value) => setModalConsentConfirmed(value === true)}
                disabled={isSubmitting}
              />
              <Label
                htmlFor={`modal-consent-confirmed-${formId}`}
                className="text-sm leading-6 text-slate-700 dark:text-slate-200"
              >
                {formCopy.modalConsentCheckbox}
              </Label>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <Checkbox
                  id={`delivery-confirmed-${formId}`}
                  checked={deliveryConfirmed}
                  onCheckedChange={(value) => setDeliveryConfirmed(value === true)}
                  disabled={isSubmitting}
                />
                <Label
                  htmlFor={`delivery-confirmed-${formId}`}
                  className="text-sm leading-6 text-slate-700 dark:text-slate-200"
                >
                  {formCopy.deliveryCheckbox}
                </Label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id={`marketing-consent-${formId}`}
                  checked={marketingConsent}
                  onCheckedChange={(value) => setMarketingConsent(value === true)}
                  disabled={isSubmitting}
                />
                <Label
                  htmlFor={`marketing-consent-${formId}`}
                  className="text-sm leading-6 text-slate-700 dark:text-slate-200"
                >
                  {formCopy.marketingCheckbox}
                </Label>
              </div>
            </>
          )}
        </div>

        {isModalVariant && isSubmitting ? (
          <div className="rounded-3xl border border-sky-200/70 bg-sky-50/80 p-4 dark:border-sky-400/20 dark:bg-sky-500/10">
            <div className="flex items-start gap-3">
              <div className={`flex size-10 shrink-0 items-center justify-center rounded-2xl ${accentIconClassName}`}>
                <ReloadIcon className="size-4 animate-spin" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {formCopy.preparingStatusLabel}
                </p>
                <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {formCopy.preparingStatusDescription}
                </p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <span className="h-1.5 animate-pulse rounded-full bg-sky-400/60" />
              <span className="h-1.5 animate-pulse rounded-full bg-sky-400/45 [animation-delay:120ms]" />
              <span className="h-1.5 animate-pulse rounded-full bg-sky-400/30 [animation-delay:240ms]" />
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="flex items-start gap-3 rounded-3xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-200">
            <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0" />
            <p className="leading-6">{error}</p>
          </div>
        ) : null}

        <div
          className={cn(
            'flex flex-col gap-3',
            isPageVariant || isModalVariant
              ? 'sm:flex-row sm:items-center sm:justify-between'
              : undefined
          )}
        >
          <div className="text-xs leading-6 text-slate-500 dark:text-slate-400">
            {formCopy.backgroundHint}
          </div>
          <Button
            type="submit"
            disabled={isSubmitting}
            className={cn(
              'h-12 rounded-full bg-slate-950 px-5 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white',
              isModalVariant ? 'w-full sm:w-auto sm:shrink-0' : undefined
            )}
          >
            {isSubmitting ? (
              <>
                <ReloadIcon className="mr-2 size-4 animate-spin" />
                {formCopy.submitting}
              </>
            ) : (
              <>
                {formCopy.submit}
                <ArrowRightIcon className="ml-2 size-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </form>
  )
}
